import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CreditCard, Shield, UserCog, Compass } from 'lucide-react';
import SecuritySettings from './SecuritySettings';
import UserProfileSettings from './UserProfileSettings';
import DisplayCurrencySetting from './DisplayCurrencySetting';
import TaxDataPackSettings from './TaxDataPackSettings';
import PrivateIntroduction from './PrivateIntroduction';
import WalkthroughVideos from './WalkthroughVideos';
import SubscriptionStatus from './SubscriptionStatus';
import ChoosePlanSection from './subscription/ChoosePlanSection';
import PaymentMethodPanel from './subscription/PaymentMethodPanel';
import AccountDataManagement from './AccountDataManagement';
import MembershipBadgeCard from './settings/MembershipBadgeCard';
import PasswordPolicyNudge from './settings/PasswordPolicyNudge';
import AssistedOnboardingCard from './settings/AssistedOnboardingCard';
import { LockedFeaturePanel } from './upgrade/LockedFeaturePanel';

/**
 * SettingsTab — Extracted from Dashboard.js.
 *
 * Session 263 (Feb 2026): reorganised the entire tab into four sub-tabs so
 * founders no longer scroll past 12 stacked cards. Landing tab is
 * "Subscription" (matches the historical top-of-page ordering). Cross-tab
 * jumps are validated by /app/scripts/cross_tab_jump_audit.py which now
 * also audits the sub-tab event `mif:go-to-settings-subtab`.
 *
 * Valid sub-tab values (kept in one place so the audit script can grep
 * them): 'subscription', 'security', 'account', 'concierge'.
 */
const SettingsTab = ({
  user,
  eligibility,
  sharingEligible,
  onGoToSharing,
  onQuickSetup,
  onDeepOnboarding,
  onJoinSession,
  onUpgradeClick,
}) => {
  const [activeSubTab, setActiveSubTab] = useState('subscription');

  // --- Sub-tab jump listener --------------------------------------------
  // Fired by external callers, e.g. the "Enable 2FA" banner on Overview
  // (mapped from 'security' via the legacy `mif:scroll-settings-to` event
  // below) and the Password Policy nudge (which needs to land users on
  // Account Settings → Change Password).
  useEffect(() => {
    const handler = (event) => {
      const target = event?.detail;
      if (typeof target === 'string') setActiveSubTab(target);
    };
    window.addEventListener('mif:go-to-settings-subtab', handler);
    return () => window.removeEventListener('mif:go-to-settings-subtab', handler);
  }, []);

  // --- Legacy scroll-into-view listener --------------------------------
  // DashboardWelcome fires 'mif:scroll-settings-to' with a section id
  // when the user clicks "Set up 2FA in Security" on the Overview banner.
  // With the new sub-tab layout the whole Security block is a single tab,
  // so we just switch to that tab — no scroll gymnastics required.
  useEffect(() => {
    const handler = (event) => {
      const id = event?.detail;
      if (id === 'security') setActiveSubTab('security');
    };
    window.addEventListener('mif:scroll-settings-to', handler);
    return () => window.removeEventListener('mif:scroll-settings-to', handler);
  }, []);

  // --- Password nudge deep-link ----------------------------------------
  // PasswordPolicyNudge fires 'mif:open-account-section' with 'password'.
  // Change Password lives inside UserProfileSettings (Account Settings
  // sub-tab), so we hop tabs first, then let UserProfileSettings handle
  // the inner sub-section on its own listener.
  useEffect(() => {
    const handler = (event) => {
      if (event?.detail === 'password') setActiveSubTab('account');
    };
    window.addEventListener('mif:open-account-section', handler);
    return () => window.removeEventListener('mif:open-account-section', handler);
  }, []);

  const subTabTrigger =
    'data-[state=active]:bg-emerald-600 data-[state=active]:text-white ' +
    'data-[state=active]:shadow-md data-[state=active]:font-semibold ' +
    'py-2.5 px-4 text-sm sm:text-base font-medium text-slate-700 ' +
    'hover:text-slate-900 transition';

  return (
    <div className="space-y-6" data-testid="settings-tab-wrapper">
      <div>
        <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Settings</h2>
        <p className="body-text mt-1">Manage your subscription, security, account details and support.</p>
      </div>

      {/* Password Policy nudge stays above the sub-tabs so it's visible on
          any landing tab — the nudge itself hops the user to Account
          Settings → Change Password when clicked. */}
      <PasswordPolicyNudge user={user} />

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full" data-testid="settings-subtabs">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full bg-slate-100 p-1.5 gap-1.5 h-auto rounded-lg border border-slate-200">
          <TabsTrigger value="subscription" data-testid="settings-subtab-subscription" className={subTabTrigger}>
            <CreditCard className="w-4 h-4 mr-2" />
            Subscription
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-subtab-security" className={subTabTrigger}>
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="settings-subtab-account" className={subTabTrigger}>
            <UserCog className="w-4 h-4 mr-2" />
            Account Settings
          </TabsTrigger>
          <TabsTrigger value="concierge" data-testid="settings-subtab-concierge" className={subTabTrigger}>
            <Compass className="w-4 h-4 mr-2" />
            Concierge &amp; Guidance
          </TabsTrigger>
        </TabsList>

        {/* Subscription --------------------------------------------------- */}
        <TabsContent value="subscription" className="mt-4 space-y-6" forceMount>
          <div hidden={activeSubTab !== 'subscription'} className="space-y-6">
            <MembershipBadgeCard user={user} onUpgradeClick={onUpgradeClick} />
            <div id="choose-your-plan-anchor">
              <ChoosePlanSection user={user} />
            </div>
            <div>
              <SubscriptionStatus user={user} />
            </div>
            <div>
              <PaymentMethodPanel user={user} onUserUpdate={() => window.location.reload()} />
            </div>
          </div>
        </TabsContent>

        {/* Security ------------------------------------------------------- */}
        <TabsContent value="security" className="mt-4" forceMount>
          <div hidden={activeSubTab !== 'security'}>
            <div
              id="settings-security-section"
              className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6"
              data-testid="settings-security-section"
            >
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">Security</h3>
              <SecuritySettings />
            </div>
          </div>
        </TabsContent>

        {/* Account Settings ---------------------------------------------- */}
        <TabsContent value="account" className="mt-4 space-y-6" forceMount>
          <div hidden={activeSubTab !== 'account'} className="space-y-6">
            <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">Account Settings</h3>
              <p className="body-text mt-1 mb-4">Manage your account settings</p>
              <UserProfileSettings user={user} onUserUpdate={() => {}} />
            </div>
            <DisplayCurrencySetting user={user} onUserUpdate={() => {}} />
            <LockedFeaturePanel
              featureLabel="Tax Data Pack"
              description="A tax-year-scoped CSV/XLSX/PDF bundle for your accountant. Available on Portfolio Clarity."
            >
              <TaxDataPackSettings
                user={user}
                onUserUpdate={() => {}}
                sharingEligible={sharingEligible}
                onGoToSharing={onGoToSharing}
              />
            </LockedFeaturePanel>
            {/* Download / Delete Account — lives inside Account Settings so
                every account-lifecycle action (profile, currency, delete)
                sits under one roof. Session 264. */}
            <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6">
              <AccountDataManagement onAccountDeleted={() => { window.location.href = '/'; }} />
            </div>
          </div>
        </TabsContent>

        {/* Concierge & Guidance ----------------------------------------- */}
        <TabsContent value="concierge" className="mt-4 space-y-6" forceMount>
          <div hidden={activeSubTab !== 'concierge'} className="space-y-6">
            {eligibility?.eligible && (
              <div id="private-introduction-section" className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Private Introductions</h3>
                <p className="body-text mt-1 mb-4">Manage Private Introductions</p>
                <PrivateIntroduction user={user} />
              </div>
            )}

            <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6" data-testid="walkthrough-videos-section">
              <WalkthroughVideos />
            </div>

            <AssistedOnboardingCard
              onQuickSetup={onQuickSetup}
              onDeepOnboarding={onDeepOnboarding}
              onJoinSession={onJoinSession}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsTab;
